import { supabase } from '@/lib/supabase';

import { type PresenceChannel } from './usePresenceSession';

type RealtimeChannel = ReturnType<typeof supabase.channel>;

/** The single broadcast event all field-presence messages ride (ADR 0002). */
const PRESENCE_EVENT = 'presence';

/**
 * Removals still in flight, by topic. supabase-js dedupes channels by topic —
 * `channel(topic)` returns the existing instance until `removeChannel` (which
 * resolves only after the leave round-trip) has finished tearing it down. A
 * join issued in that window re-subscribes the dying instance, and the late
 * teardown then lands on the live channel: it keeps receiving messages
 * internally but never fires callbacks (peer claims freeze) and its sends
 * stop reaching the wire. Deferring each join until the topic's pending
 * removal resolves closes the window.
 */
const pendingRemovals = new Map<string, Promise<unknown>>();

/**
 * The production PresenceChannel: a private Supabase Realtime channel, so
 * joining is authorized by RLS on realtime.messages (the template presence
 * policies), carrying field presence as broadcast messages only — never
 * Realtime presence, whose rate limits and diff semantics this replaced
 * (ADR 0002).
 *
 * The underlying channel is created lazily in subscribe() (after any pending
 * removal of the same topic); a send before the join lands is a no-op, which
 * is safe because the session hook only sends once subscribed.
 */
export function createSupabaseBroadcastChannel(topic: string): PresenceChannel {
  let channel: RealtimeChannel | null = null;
  let messageCb: ((payload: Record<string, unknown>) => void) | null = null;
  let closed = false;

  return {
    onMessage(cb) {
      messageCb = cb;
    },
    subscribe(onStatus) {
      void (pendingRemovals.get(topic) ?? Promise.resolve()).then(() => {
        if (closed) return;
        channel = supabase.channel(topic, {
          config: { private: true },
        });
        if (messageCb) {
          const cb = messageCb;
          channel.on('broadcast', { event: PRESENCE_EVENT }, (message) => {
            const payload = (message as { payload?: unknown }).payload;
            if (payload && typeof payload === 'object') {
              cb(payload as Record<string, unknown>);
            }
          });
        }
        channel.subscribe((status) => onStatus(status));
      });
    },
    send(payload) {
      return (
        channel?.send({ type: 'broadcast', event: PRESENCE_EVENT, payload }) ??
        Promise.resolve('ok')
      );
    },
    unsubscribe() {
      closed = true;
      const current = channel;
      if (!current) return Promise.resolve('ok');
      const removal = supabase.removeChannel(current).finally(() => {
        if (pendingRemovals.get(topic) === removal) pendingRemovals.delete(topic);
      });
      pendingRemovals.set(topic, removal);
      return removal;
    },
  };
}
